import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  fetchAdminReport,
  resolveAdminReport,
  resolveAdminAppeal,
  liftUserBan,
  categoryPenalty,
  formatIsoDate,
  REPORT_OUTCOMES,
  BAN_DURATIONS,
} from '../../lib/reportsAdminApi';
import { usePortalT } from '../../i18n/usePortalT';
import {
  PageTitle,
  PageSubtitle,
  Btn,
  BtnDanger,
  BtnRow,
  Card,
  Badge,
  ErrorText,
  Field,
} from '../../styles/adminStyles';

function InfoRow({ label, children }) {
  return (
    <div style={{ display: 'flex', gap: 12, padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
      <div style={{ width: 120, color: '#64748b', fontSize: 13, fontWeight: 700 }}>{label}</div>
      <div style={{ flex: 1, fontSize: 14 }}>{children}</div>
    </div>
  );
}

export default function ReportDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, tEnum, language } = usePortalT();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const [outcome, setOutcome] = useState('success_normal');
  const [banDuration, setBanDuration] = useState('1m');
  const [detail, setDetail] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetchAdminReport(id);
      setReport(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const onResolve = async () => {
    if (busy) return;
    const isMax = outcome === 'success_max';
    if (!window.confirm(isMax ? t('reports.confirmMax') : t('reports.confirmResolve'))) return;
    setBusy(true);
    setMsg(null);
    setError(null);
    const res = await resolveAdminReport(id, {
      outcome,
      banDuration: isMax ? banDuration : null,
      detail: detail.trim() || null,
    });
    setBusy(false);
    if (res.success) {
      setMsg(t('reports.resolveDone'));
      setDetail('');
      load();
    } else {
      setError(res.error || t('reports.resolveFailed'));
    }
  };

  const onResolveAppeal = async (appealId, decision) => {
    if (busy) return;
    let restoreCredit = 0;
    let note = '';
    if (decision === 'accept') {
      const raw = window.prompt(t('reports.promptRestoreAccept'), '0');
      if (raw === null) return;
      restoreCredit = Math.max(0, Math.min(100, Number(raw) || 0));
      note = window.prompt(t('reports.promptNote'), '') || '';
    } else {
      note = window.prompt(t('reports.promptRejectReason'), '') || '';
    }
    setBusy(true);
    setError(null);
    const res = await resolveAdminAppeal(appealId, { decision, note: note || null, restoreCredit });
    setBusy(false);
    if (res.success) {
      setMsg(decision === 'accept' ? t('reports.appealAccepted') : t('reports.appealRejected'));
      load();
    } else {
      setError(res.error || t('reports.appealFailed'));
    }
  };

  const onLiftBan = async () => {
    if (busy || !report) return;
    const raw = window.prompt(t('reports.promptRestoreLift'), '0');
    if (raw === null) return;
    const restoreCredit = Math.max(0, Math.min(100, Number(raw) || 0));
    const note = window.prompt(t('reports.promptNote'), '') || '';
    setBusy(true);
    setError(null);
    const res = await liftUserBan(report.targetUserId, { restoreCredit, note: note || null, reportId: report.id });
    setBusy(false);
    if (res.success) {
      setMsg(t('reports.banLifted'));
      load();
    } else {
      setError(res.error || t('reports.liftBanFailed'));
    }
  };

  if (loading) return <p>{t('common.loading')}</p>;
  if (error && !report) return <ErrorText>{error}</ErrorText>;
  if (!report) return <Card>{t('reports.notFound')}</Card>;

  const penalty = categoryPenalty(report.category);

  return (
    <div>
      <BtnRow style={{ marginTop: 0, marginBottom: 12 }}>
        <Btn type="button" onClick={() => navigate('/admin/reports')}>{t('common.backToList')}</Btn>
      </BtnRow>

      <PageTitle>{t('reports.detailTitle')} · {tEnum('reportCategory', report.category)}</PageTitle>
      <PageSubtitle>
        <Badge $status={report.status === 'pending' ? 'active' : 'ended'}>{tEnum('reportStatus', report.status)}</Badge>
        {'　'}{t('reports.reportId')}：{report.id}
      </PageSubtitle>

      {msg ? <p style={{ color: '#15803d', fontWeight: 700 }}>{msg}</p> : null}
      {error ? <ErrorText>{error}</ErrorText> : null}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 360px)', gap: 16, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Card>
            <h3 style={{ margin: '0 0 8px' }}>{t('reports.reportDetail')}</h3>
            <InfoRow label={t('reports.colCategory')}>{tEnum('reportCategory', report.category)}（{t('reports.suggestedDeduct', { n: penalty })}）</InfoRow>
            <InfoRow label={t('reports.reporter')}>{report.reporterName}</InfoRow>
            <InfoRow label={t('reports.target')}>
              {report.targetName}
              {report.targetBannedUntil ? (
                <span style={{ marginLeft: 8, color: '#b91c1c', fontWeight: 800, fontSize: 12 }}>
                  {t('reports.bannedUntil', { date: formatIsoDate(report.targetBannedUntil, language) })}
                </span>
              ) : null}
            </InfoRow>
            <InfoRow label={t('reports.targetCredit')}>{report.targetCreditScore ?? '—'}</InfoRow>
            {report.eventId ? <InfoRow label={t('reports.relatedEvent')}>{report.eventId}</InfoRow> : null}
            <InfoRow label={t('reports.reportNote')}>{report.description || `（${t('common.none')}）`}</InfoRow>
            <InfoRow label={t('reports.submittedAt')}>{formatIsoDate(report.createdAt, language)}</InfoRow>
            {report.resolvedAt ? (
              <InfoRow label={t('reports.resolution')}>
                {tEnum('reportStatus', report.status)} · {report.resolution || '—'}<br />
                {report.resolutionDetail ? <span style={{ color: '#64748b' }}>{report.resolutionDetail}</span> : null}
                <div style={{ fontSize: 12, color: '#94a3b8' }}>{formatIsoDate(report.resolvedAt, language)}</div>
              </InfoRow>
            ) : null}
          </Card>

          <Card>
            <h3 style={{ margin: '0 0 8px' }}>{t('reports.violationHistory')}</h3>
            {Array.isArray(report.targetViolationHistory) && report.targetViolationHistory.length > 0 ? (
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#475569' }}>
                {report.targetViolationHistory.map((v, i) => (
                  <li key={i} style={{ marginBottom: 4 }}>
                    {tEnum('reportCategory', v.category)} · {v.amount != null ? `${v.amount}` : ''} · {formatIsoDate(v.confirmedAt, language)}
                  </li>
                ))}
              </ul>
            ) : (
              <p style={{ color: '#94a3b8', margin: 0 }}>{t('reports.noViolations')}</p>
            )}
          </Card>

          <Card>
            <h3 style={{ margin: '0 0 8px' }}>{t('reports.appealRecords')}</h3>
            {Array.isArray(report.appeals) && report.appeals.length > 0 ? (
              report.appeals.map((ap) => (
                <div key={ap.id} style={{ borderTop: '1px solid #f1f5f9', paddingTop: 10, marginTop: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Badge $status={ap.status === 'pending' ? 'active' : 'ended'}>{tEnum('appealStatus', ap.status)}</Badge>
                    <span style={{ fontSize: 12, color: '#94a3b8' }}>{formatIsoDate(ap.createdAt, language)}</span>
                  </div>
                  <p style={{ fontSize: 14, margin: '8px 0' }}>{ap.reason || t('reports.noReason')}</p>
                  {ap.decisionNote ? (
                    <p style={{ fontSize: 12, color: '#64748b', margin: '4px 0' }}>{t('reports.decisionNote')}{ap.decisionNote}</p>
                  ) : null}
                  {ap.status === 'pending' ? (
                    <BtnRow style={{ marginTop: 8 }}>
                      <Btn $primary type="button" disabled={busy} onClick={() => onResolveAppeal(ap.id, 'accept')}>{t('reports.accept')}</Btn>
                      <BtnDanger type="button" disabled={busy} onClick={() => onResolveAppeal(ap.id, 'reject')}>{t('reports.reject')}</BtnDanger>
                    </BtnRow>
                  ) : null}
                </div>
              ))
            ) : (
              <p style={{ color: '#94a3b8', margin: 0 }}>{t('reports.noAppeals')}</p>
            )}
          </Card>
        </div>

        <Card>
          <h3 style={{ margin: '0 0 12px' }}>{t('reports.decision')}</h3>
          <Field>
            {t('reports.outcome')}
            <select value={outcome} onChange={(e) => setOutcome(e.target.value)}>
              {REPORT_OUTCOMES.map((o) => (
                <option key={o.value} value={o.value}>{tEnum('reportOutcome', o.value)}</option>
              ))}
            </select>
          </Field>
          {outcome === 'success_max' ? (
            <Field>
              {t('reports.banDuration')}
              <select value={banDuration} onChange={(e) => setBanDuration(e.target.value)}>
                {BAN_DURATIONS.map((d) => (
                  <option key={d.value} value={d.value}>{tEnum('banDuration', d.value)}</option>
                ))}
              </select>
            </Field>
          ) : null}
          <Field>
            {t('reports.noteToUser')}
            <textarea value={detail} onChange={(e) => setDetail(e.target.value)} placeholder={t('reports.notePh')} />
          </Field>
          <BtnRow style={{ marginTop: 4 }}>
            <Btn $primary type="button" disabled={busy} onClick={onResolve}>
              {busy ? t('common.processing') : t('reports.execute')}
            </Btn>
          </BtnRow>

          {report.targetBannedUntil ? (
            <div style={{ marginTop: 16, borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>
              <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 8px' }}>{t('reports.currentlyBanned')}</p>
              <BtnDanger type="button" disabled={busy} onClick={onLiftBan}>{t('reports.liftBan')}</BtnDanger>
            </div>
          ) : null}

          <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 16 }}>
            {t('reports.penaltyRule')}
          </p>
        </Card>
      </div>

      <BtnRow style={{ marginTop: 20 }}>
        <Link to="/admin/appeals"><Btn>{t('reports.viewAllAppeals')}</Btn></Link>
      </BtnRow>
    </div>
  );
}
